import {
  createRootRoute, createRoute, createRouter, Navigate, Outlet,
} from '@tanstack/react-router';
import { Shell } from './shell';
import { useMe } from './session';
import { ServersPage } from './routes/servers';
import { ImpostazioniPage } from './routes/impostazioni';
import { AssistentiPage } from './routes/assistenti';
import { CollegaPage, validaCercaCollega } from './routes/collega';
import { RegistroPage } from './routes/registro';
import { NewServerPage } from './routes/server-new';
import { ServerPage } from './routes/server';
import { InstancesPage } from './routes/instances';
import { InstancePage } from './routes/instance';
import { BackupPage } from './routes/backup';
import { ContinuitaPage, CorsaPage } from './routes/continuita';
import { SorgentePage, TraslochiPage } from './routes/traslochi';
import { TraslocoPage } from './routes/trasloco';
import { IPPage } from './routes/ip';
import { CatalogoPage, RepoCatalogoPage } from './routes/catalogo';
import { BlueprintEditPage, BlueprintPage } from './routes/blueprint';
import { JobsPage } from './routes/jobs';
import { JobPage } from './routes/job';
import { MembersPage } from './routes/members';
import { AccessiPage } from './routes/accessi';
import { Errore, NonTrovato } from './routes/errore';
import { InvitoPage } from './routes/invito';

// La radice non è lo shell: è solo il punto d'innesto. Lo shell è una rotta di
// impaginazione senza percorso proprio, che avvolge le schermate autenticate.
//
// Metterlo sulla radice sembrava equivalente e non lo è: `Shell` su 401
// restituisce la schermata d'ingresso **senza** rendere l'Outlet, quindi ogni
// rotta figlia — compresa quella dell'invito, che deve funzionare proprio per
// chi non è ancora entrato — non veniva mai raggiunta.
const rootRoute = createRootRoute({ component: Outlet });

const shellRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'shell',
  component: Shell,
});

/** La radice non è una schermata: manda alla prima organizzazione dell'utente. */
function Index() {
  const me = useMe();
  const slug = me.data?.organizations?.[0]?.slug;
  if (!slug) return null;
  return <Navigate to="/orgs/$slug/servers" params={{ slug }} replace />;
}

const indexRoute = createRoute({ getParentRoute: () => shellRoute, path: '/', component: Index });

/**
 * L'invito sta fuori dallo shell: è l'unica rotta che deve funzionare per chi
 * non è ancora in nessuna organizzazione. Dentro, `Shell` mostrerebbe «nessuna
 * organizzazione» prima che l'invito possa essere letto.
 */
/**
 * Il consenso di un assistente, fuori dallo shell per la stessa ragione
 * dell'invito: chi arriva da claude.ai può non essere ancora entrato.
 */
const collegaRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/collega',
  validateSearch: validaCercaCollega,
  component: CollegaPage,
});

const invitoRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/invito',
  validateSearch: (search: Record<string, unknown>) => ({
    token: typeof search.token === 'string' ? search.token : '',
  }),
  component: InvitoPage,
});

const orgRoute = createRoute({
  getParentRoute: () => shellRoute,
  path: '/orgs/$slug',
  component: Outlet,
});

const serversRoute = createRoute({ getParentRoute: () => orgRoute, path: '/servers', component: ServersPage });
const impostazioniRoute = createRoute({ getParentRoute: () => orgRoute, path: '/impostazioni', component: ImpostazioniPage });
const assistentiRoute = createRoute({ getParentRoute: () => orgRoute, path: '/assistenti', component: AssistentiPage });
// `?risorsa=server&id=<id>` è il link «Registro di questo server» dalle altre
// schermate; `chi` e `cosa` sono i filtri della pagina, nell'URL perché un
// registro filtrato si passa a un collega come link.
const registroRoute = createRoute({
  getParentRoute: () => orgRoute,
  path: '/registro',
  validateSearch: (search: Record<string, unknown>): { risorsa?: string; id?: string; chi?: string; cosa?: string } =>
    Object.fromEntries(
      (['risorsa', 'id', 'chi', 'cosa'] as const)
        .filter((k) => typeof search[k] === 'string' && search[k] !== '')
        .map((k) => [k, search[k] as string]),
    ),
  component: RegistroPage,
});
const newServerRoute = createRoute({ getParentRoute: () => orgRoute, path: '/servers/new', component: NewServerPage });
const serverRoute = createRoute({ getParentRoute: () => orgRoute, path: '/servers/$id', component: ServerPage });
// `?nuova=<server>` apre il modulo della nuova istanza già sul server scelto,
// `?istanza=<id>` apre il pannello dei backup di quell'istanza: sono i link
// con cui le altre schermate mandano qui senza far cercare la riga giusta.
const instancesRoute = createRoute({
  getParentRoute: () => orgRoute,
  path: '/instances',
  validateSearch: (search: Record<string, unknown>): { nuova?: string } =>
    typeof search.nuova === 'string' ? { nuova: search.nuova } : {},
  component: InstancesPage,
});
const instanceRoute = createRoute({ getParentRoute: () => orgRoute, path: '/instances/$id', component: InstancePage });
const backupRoute = createRoute({
  getParentRoute: () => orgRoute,
  path: '/backup',
  validateSearch: (search: Record<string, unknown>): { istanza?: string } =>
    typeof search.istanza === 'string' ? { istanza: search.istanza } : {},
  component: BackupPage,
});
const continuitaRoute = createRoute({ getParentRoute: () => orgRoute, path: '/continuita', component: ContinuitaPage });
const corsaRoute = createRoute({ getParentRoute: () => orgRoute, path: '/continuita/$id', component: CorsaPage });
const traslochiRoute = createRoute({ getParentRoute: () => orgRoute, path: '/traslochi', component: TraslochiPage });
const sorgenteRoute = createRoute({ getParentRoute: () => orgRoute, path: '/traslochi/sorgenti/$id', component: SorgentePage });
const traslocoRoute = createRoute({ getParentRoute: () => orgRoute, path: '/traslochi/$id', component: TraslocoPage });
const catalogoRoute = createRoute({ getParentRoute: () => orgRoute, path: '/catalogo', component: CatalogoPage });
const repoCatalogoRoute = createRoute({ getParentRoute: () => orgRoute, path: '/catalogo/$owner/$name', component: RepoCatalogoPage });
const blueprintRoute = createRoute({ getParentRoute: () => orgRoute, path: '/blueprint', component: BlueprintPage });
const blueprintEditRoute = createRoute({ getParentRoute: () => orgRoute, path: '/blueprint/$id', component: BlueprintEditPage });
const ipRoute = createRoute({ getParentRoute: () => orgRoute, path: '/ip', component: IPPage });
const jobsRoute = createRoute({ getParentRoute: () => orgRoute, path: '/jobs', component: JobsPage });
const jobRoute = createRoute({ getParentRoute: () => orgRoute, path: '/jobs/$id', component: JobPage });
const membersRoute = createRoute({ getParentRoute: () => orgRoute, path: '/members', component: MembersPage });
const accessiRoute = createRoute({ getParentRoute: () => orgRoute, path: '/accessi', component: AccessiPage });

const routeTree = rootRoute.addChildren([
  // Fuori dallo shell, di proposito.
  invitoRoute,
  collegaRoute,
  shellRoute.addChildren([
    indexRoute,
    orgRoute.addChildren([
      serversRoute, newServerRoute, serverRoute,
      instancesRoute, instanceRoute,
      catalogoRoute, repoCatalogoRoute,
      blueprintRoute, blueprintEditRoute,
      backupRoute,
      continuitaRoute, corsaRoute, traslochiRoute, sorgenteRoute, traslocoRoute, ipRoute,
      jobsRoute, jobRoute, membersRoute, accessiRoute,
      impostazioniRoute,
      assistentiRoute,
      registroRoute,
    ]),
  ]),
]);

export const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
  // Entrambi rendono **dentro** lo shell: chi si perde in un URL sbagliato
  // resta dov'era, con la navigazione a portata di mano, invece di trovarsi
  // una pagina isolata da cui l'unica uscita è il tasto indietro.
  defaultNotFoundComponent: NonTrovato,
  defaultErrorComponent: ({ error, reset }) => <Errore error={error} reset={reset} />,
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
